import { cancelFrame, frame } from 'motion';
import { type Transition, motion, useAnimationControls, useReducedMotion } from 'motion/react';
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Icon } from '@column/column-ui-kit';

// Matches the FormField grid so these rows line up with the page's other
// fields: a label/description column on the left and the control on the right.
// The control here is a slim timeline rail plus the account card, so the whole
// block reads as two labelled fields connected by a maps-style timeline.

const FIELD_BREAKPOINT = '464px'; // theme.containerBreakpoints.tablet
const FIELD_BREAKPOINT_WIDE = '956px'; // theme.containerBreakpoints.wide

// The controls are h-9 (36px) with vertically-centered text, so their text — and
// therefore the dot beside it — sits 18px from the top of the control row.
const DOT_CENTER_PX = 18;
const CONTROL_CENTER = `${DOT_CENTER_PX}px`;

// The label's line-height; half of it is the label's vertical center, used both
// to align the wide-layout label with the dot and to align the dot with the label
// on the narrow (stacked) layout.
const LABEL_LINE_HEIGHT_PX = 18;

// Marker + connector sizing, matched to the transfer-page timeline scale.
const DOT_SIZE_PX = 20;
// The marker icon draws a circle of radius 7.5 with a 1.5 stroke in a 20px box,
// so its visible edge is ~8.25px from center. Inset the line by that, plus the
// line's 1px round-cap overhang, plus a 4px gap — leaving a true 4px space
// between each circle's edge and the connector.
const LINE_INSET_PX = 8.25 + 1 + 4;

// Gap between the rail (marker column) and the account card within a control.
const CONTROL_GAP_PX = 12;
// Padding between the wrapping frame's edge and the control cluster it groups.
const FRAME_PADDING_PX = 16;
// On narrow (stacked) layouts the label/description are indented to the card's
// left edge — the rail's width plus the control gap — so the connector line keeps
// a clear path straight down the left and the text never crosses it.
const LABEL_INDENT_PX = DOT_SIZE_PX + CONTROL_GAP_PX;

// The flow icon that sits mid-track, and the half-height of the gap punched into
// the connector line around it so the line stops short on both sides and the icon
// never intersects it (see measure + LineSvg).
const FLOW_ICON_SIZE_PX = 20;
const LINE_GAP_HALF_PX = 13;

// Duration of the row swap and the accompanying label cross-fade.
const SWAP_DURATION = 0.45;

// One pass of a chevron travelling the length of the gap: it starts small and
// faint above centre, grows to full size and opacity as it slides to centre
// (taking the previous chevron's place), then keeps sliding down at full size
// while fading out. Two chevrons run this offset by half a period, so there's
// always one growing in at the top and one leaving at the bottom — a continuous
// downward march. Opacity is 0 at both ends, so the wrap-around is invisible.
const chevronFlow = keyframes`
  0% {
    transform: translateY(-7px) scale(0.4);
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  50% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  80% {
    opacity: 0.35;
  }
  100% {
    transform: translateY(7px) scale(1);
    opacity: 0;
  }
`;

const FLOW_DURATION = '1.8s';

const Layout = styled.div`
  container-type: inline-size;
  container-name: form-field;
`;

// The grouping surface, shared by the wide-layout control-column frame and the
// narrow-layout whole-section box. A recessed off-white fill defined by a soft
// border; in dark it drops one step below the gray-900 content surface with a
// low-alpha white edge, matching the other dark fields.
const wrapperSurface = css`
  border-radius: 12px;
  background: #f9f9fa;
  border: 1px solid var(--color-gray-100);

  &:is(.dark *) {
    background: var(--color-gray-950);
    border-color: rgb(255 255 255 / 0.1);
  }
`;

// Wraps the List. On wide layouts the measured ControlFrame frames just the
// control column, and this only offsets the rows down so the frame's 16px top
// padding lines up with the field gap above (see ControlFrame). On narrow
// (stacked) layouts there's no separate control column to frame, so instead wrap
// the whole section — labels, descriptions and both controls — in the surface
// directly. Lives here rather than on Layout (its own query container) or List
// (whose padding would desync the connector-line math).
const Inner = styled.div`
  @container form-field (width < ${FIELD_BREAKPOINT}) {
    ${wrapperSurface}
    padding: ${FRAME_PADDING_PX}px;
  }

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    padding-top: ${FRAME_PADDING_PX}px;
  }
`;

const List = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    gap: 24px;
  }
`;

// The connecting line is a single SVG that lives outside the rows, so it stays
// put while the rows swap — the markers slide along it. Its position is measured
// from the rails (offsetTop/Left ignore the swap's FLIP transforms, so it sits
// at the resting layout and never jumps mid-animation). It tracks the markers at
// every width — on narrow layouts the labels indent out of its way (see Title) so
// the timeline stays intact instead of being hidden.
const LineSvg = styled.svg`
  display: block;
  position: absolute;
  z-index: 0;
  width: 2px;
  overflow: visible;
  transform: translateX(-50%);
  pointer-events: none;

  line {
    stroke-width: 2;
    stroke-linecap: round;
    stroke: var(--color-border);
  }
`;

// The direction indicator that sits in the gap punched into the connector line.
// Positioned imperatively at the track's midpoint (see measure); centred on the
// same x as the line so it reads as part of the timeline. It takes the line's
// colour so the chevrons read as part of the same rail. Overflow is visible so
// the chevrons can slide past the 20px box as they fade in and out.
const FlowIcon = styled.div`
  position: absolute;
  z-index: 1;
  width: ${FLOW_ICON_SIZE_PX}px;
  height: ${FLOW_ICON_SIZE_PX}px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  color: var(--color-border);
`;

// Each chevron is a full-box SVG overlaying the icon; the animation runs on the
// inner <path> (not the SVG or an HTML wrapper) so the scale is part of the SVG's
// own transform, which vector-effect: non-scaling-stroke then cancels — the shape
// grows and shrinks while the 1.5px stroke stays put. (Scaling an HTML wrapper
// would rasterise then composite-scale the whole glyph, stroke included, so the
// attribute never gets a chance to act.) overflow is visible so the path can
// travel past the 20px box as it fades in and out. The two chevrons run the loop
// offset by half the period — one always arriving at centre while the other
// leaves below it. On reduced-motion the animation is dropped and each is pinned
// to its resting place (small above, large at centre) via $variant.
const ChevronSvg = styled.svg<{ $offset?: boolean; $variant?: 'small' | 'large' }>`
  position: absolute;
  inset: 0;
  display: block;
  overflow: visible;

  path {
    /* Scale about the path's own centre so it grows/shrinks in place. */
    transform-box: fill-box;
    transform-origin: center;
    animation: ${chevronFlow} ${FLOW_DURATION} linear infinite;
  }

  ${({ $offset }) =>
    $offset &&
    css`
      path {
        animation-delay: calc(-${FLOW_DURATION} / 2);
      }
    `}

  ${({ $variant }) =>
    $variant &&
    css`
      path {
        animation: none;
        opacity: 1;
        transform: ${$variant === 'small' ? 'translateY(-4px) scale(0.4)' : 'translateY(0) scale(1)'};
      }
    `}
`;

// A single down-chevron matching the larger of the two supplied chevrons, centred
// in the 20px box. non-scaling-stroke keeps its stroke at 1.5px through the scale.
const FlowChevron: React.FC<{ $offset?: boolean; $variant?: 'small' | 'large' }> = ({ $offset, $variant }) => (
  <ChevronSvg
    width={FLOW_ICON_SIZE_PX}
    height={FLOW_ICON_SIZE_PX}
    viewBox="0 0 20 20"
    fill="none"
    $offset={$offset}
    $variant={$variant}
    aria-hidden
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  </ChevronSvg>
);

// Each row is its own full-width grid (rather than a shared grid via
// display:contents) so it can be a single animatable box for the swap; equal
// widths keep the label columns aligned between rows.
const Row = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    display: grid;
    grid-template-columns: minmax(180px, 25%) 1fr;
    grid-column-gap: 24px;
    align-items: stretch;
  }

  @container form-field (min-width: ${FIELD_BREAKPOINT_WIDE}) {
    grid-template-columns: minmax(180px, 25%) auto 25%;
  }
`;

const Title = styled(motion.div)`
  display: grid;
  grid-gap: 0;
  /* Stacked above the card on narrow layouts: indent to the card's left edge so
     the label/description clear the connector line running down the left. */
  padding-left: ${LABEL_INDENT_PX}px;

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    grid-column: 1;
    grid-gap: 4px;
    /* Back in its own column beside the control — no indent needed. */
    padding-left: 0;
    /* Pin to the top so the control column's height (a tall selected card)
       never stretches this cell and pushes the description down. */
    align-self: start;
    /* Center the single-line label on CONTROL_CENTER (subtracting half the
       label line-height puts its center — and baseline — at 18px). */
    margin-top: calc(${CONTROL_CENTER} - ${LABEL_LINE_HEIGHT_PX / 2}px);
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: ${LABEL_LINE_HEIGHT_PX}px;
  font-weight: 500;
  color: var(--color-foreground);
`;

const Asterix = styled.span`
  color: var(--color-destructive);
  font-weight: 700;
  display: inline-block;
  margin-left: 2px;
`;

const Description = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: var(--color-muted-foreground);
  max-width: 100%;

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    max-width: 32ch;
  }
`;

const Control = styled.div`
  display: flex;
  gap: ${CONTROL_GAP_PX}px;
  align-items: stretch;

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    grid-column: 2;
    min-width: 0;
    /* Hug the card's height instead of stretching to the (often taller) label
       column, so the framing wrapper's top and bottom padding stay symmetric. */
    align-self: start;
    /* Inset the rail + card so they sit indented inside the wrapping frame, which
       spans the full control column — the same width as the form's other fields. */
    padding-left: ${FRAME_PADDING_PX}px;
    padding-right: ${FRAME_PADDING_PX}px;
  }
`;

const Rail = styled.div`
  flex: none;
  width: ${DOT_SIZE_PX}px;

  /* Wide: the rail is the marker's positioning context, so the dot sits beside
     the card. Narrow: the rail stays static (just reserving the left gutter) and
     the marker positions against the row instead, lifting it up to the label. */
  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    position: relative;
  }
`;

// Shares the transfer-page timeline's circle/check SVGs: a hollow gray circle
// while empty, swapping to a seafoam check (the timeline's "completed" state)
// once the field is selected.
const Marker = styled.span<{ $selected: boolean }>`
  position: absolute;
  z-index: 1;
  display: flex;
  /* Narrow (stacked): positioned against the row, in the left gutter and level
     with the label's center — so the dot lines up with "Bank Account" /
     "Counterparty" rather than the card below it. */
  top: ${LABEL_LINE_HEIGHT_PX / 2}px;
  left: ${DOT_SIZE_PX / 2}px;
  transform: translate(-50%, -50%);
  color: ${({ $selected }) => ($selected ? 'var(--color-seafoam-600)' : 'var(--color-border)')};
  transition: color 0.2s ease;

  /* Wide: positioned against the rail, level with the card's first line. */
  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    top: ${CONTROL_CENTER};
    left: 50%;
  }

  svg {
    --icon-size: ${DOT_SIZE_PX}px;
    --icon-color: currentColor;
  }
`;

const CardSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

// A grouping surface drawn behind the control column so the two account cards
// (and the timeline running between them) read as one connected unit, distinct
// from the labels/descriptions in the left column. It's absolutely positioned
// and sized imperatively from the controls' *resting* offsets (see measure) so
// it stays a static frame while the rows swap and the dots slide within it —
// FLIP transforms would otherwise make its bounding box collapse mid-animation.
// Only shown once the layout has a distinct control column (>= FIELD_BREAKPOINT);
// on the narrow stacked layout the labels sit between the controls, so there's no
// separate column to frame — Inner wraps the whole section in the surface instead.
const ControlFrame = styled.div`
  display: none;
  position: absolute;
  z-index: 0;
  pointer-events: none;
  ${wrapperSurface}

  @container form-field (min-width: ${FIELD_BREAKPOINT}) {
    display: block;
  }
`;

export interface SequenceRow {
  /** Stable key (e.g. the field name) used for React reconciliation. */
  key: string;
  label: string;
  description?: string;
  required?: boolean;
  /** Whether this field has a value — drives the dot's stroked/solid state. */
  selected?: boolean;
  /** The control rendered in the right column (an account selector card). */
  node: ReactNode;
}

export interface TransferSequenceProps {
  /** Ordered top-to-bottom: the first row is the money origin, the last the destination. */
  rows: SequenceRow[];
  className?: string;
}

/**
 * Lays out the transfer's source and destination accounts as labelled rows
 * connected by a vertical timeline. Row order encodes direction (origin → top,
 * destination → bottom); the orchestrator reorders the rows when the transfer
 * direction flips.
 *
 * On a swap each row animates to its new position (FLIP) and the dots slide
 * along the fixed connecting line, while the labels/descriptions cross-fade
 * (opacity 1 → 0 → 1) so the two columns of text never visibly overlap.
 */
export const TransferSequence: React.FC<TransferSequenceProps> = ({ rows, className }) => {
  const reduceMotion = useReducedMotion();
  const labelControls = useAnimationControls();
  const orderKey = rows.map((row) => row.key).join('>');
  const previousOrderKey = useRef(orderKey);
  // Changes on order (swap) AND selection (select/clear) — both shift the layout
  // and move the dots, so both need the connector re-measured frame by frame.
  const animKey = rows.map((row) => `${row.key}:${row.selected ? 1 : 0}`).join('>');
  const previousAnimKey = useRef(animKey);

  const duration = reduceMotion ? 0 : SWAP_DURATION;

  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const controlRefs = useRef<(HTMLDivElement | null)[]>([]);
  const markerRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const frameRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // The connector is drawn as two segments with a gap between them for the flow
  // icon; the icon is positioned at the gap's centre.
  const lineTopRef = useRef<SVGLineElement>(null);
  const lineBottomRef = useRef<SVGLineElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  // Mounts the connector once both markers exist; its geometry is then written
  // imperatively (see measure), never through React state — so the line never
  // lags a render behind the dots it connects.
  const [ready, setReady] = useState(false);

  // The descriptions change with the direction toggle. To avoid the text
  // visibly flipping before the cross-fade, we render a *displayed* copy keyed
  // by field and only swap it to the new text at the fade's midpoint (opacity 0).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [displayedDescriptions, setDisplayedDescriptions] = useState<Record<string, string | undefined>>(() =>
    Object.fromEntries(rows.map((row) => [row.key, row.description]))
  );

  // Measure the connector from the markers' live rects (relative to the List)
  // and write it straight to the SVG — no React state, so the line can never
  // paint a frame behind the dots. getBoundingClientRect includes any FLIP
  // transforms, so when this runs in motion's frame loop (after the rows' swap
  // transforms are written) it tracks the dots as they cross — the line
  // compresses and stretches with them — and settles to the resting layout
  // afterwards. Using min/max of the two centers keeps it correct regardless of
  // which dot is on top; it's also offsetParent-independent, so it never
  // collapses to zero.
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    // Size the grouping frame from the controls' *resting* offsets (offsetTop/
    // Left, summed up to the List, ignore FLIP transforms) so it stays a static
    // box while the rows swap — getBoundingClientRect would track the transforms
    // and collapse the union to a single row's height mid-animation. Horizontally
    // the frame spans the whole control column (matching the form's other fields;
    // the rail + card are inset within it by Control's own padding). Vertically it
    // adds the padding above the top control and below the bottom one — Control is
    // align-self:start, so its box hugs the card and that padding stays symmetric.
    const frameEl = frameRef.current;
    const controls = controlRefs.current.filter((c): c is HTMLDivElement => Boolean(c));
    if (frameEl && controls.length >= 2) {
      const offsetTo = (el: HTMLElement, axis: 'offsetTop' | 'offsetLeft') => {
        let value = 0;
        let node: HTMLElement | null = el;
        while (node && node !== list) {
          value += node[axis];
          node = node.offsetParent as HTMLElement | null;
        }
        return value;
      };
      const tops = controls.map((c) => offsetTo(c, 'offsetTop'));
      const lefts = controls.map((c) => offsetTo(c, 'offsetLeft'));
      const top = Math.min(...tops);
      const left = Math.min(...lefts);
      const bottom = Math.max(...controls.map((c, i) => tops[i] + c.offsetHeight));
      const right = Math.max(...controls.map((c, i) => lefts[i] + c.offsetWidth));
      frameEl.style.top = `${top - FRAME_PADDING_PX}px`;
      frameEl.style.left = `${left}px`;
      frameEl.style.width = `${right - left}px`;
      frameEl.style.height = `${bottom - top + FRAME_PADDING_PX * 2}px`;
    }

    const svg = svgRef.current;
    const lineTop = lineTopRef.current;
    const lineBottom = lineBottomRef.current;
    const markers = markerRefs.current.filter((m): m is HTMLSpanElement => Boolean(m));
    if (!svg || !lineTop || !lineBottom || markers.length < 2) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const centers = markers.map((marker) => {
      const rect = marker.getBoundingClientRect();
      return { x: rect.left - listRect.left + rect.width / 2, y: rect.top - listRect.top + rect.height / 2 };
    });
    const ys = centers.map((center) => center.y);
    const topCenter = Math.min(...ys);
    const bottomCenter = Math.max(...ys);
    const height = Math.max(0, bottomCenter - topCenter - LINE_INSET_PX * 2);
    svg.style.top = `${topCenter + LINE_INSET_PX}px`;
    svg.style.left = `${centers[0].x}px`;
    svg.style.height = `${height}px`;

    // Punch a centred gap into the line for the flow icon: draw a top segment and
    // a bottom segment that stop short of the midpoint by the gap half-height. If
    // the markers sit close together, shrink the gap so a stub of line survives on
    // each side rather than the segments crossing over.
    const mid = height / 2;
    const gapHalf = Math.min(LINE_GAP_HALF_PX, Math.max(0, mid - 4));
    lineTop.setAttribute('y2', `${Math.max(0, mid - gapHalf)}`);
    lineBottom.setAttribute('y1', `${mid + gapHalf}`);
    lineBottom.setAttribute('y2', `${height}`);

    // Centre the flow icon in the gap, on the line's x.
    if (iconRef.current) {
      iconRef.current.style.top = `${(topCenter + bottomCenter) / 2}px`;
      iconRef.current.style.left = `${centers[0].x}px`;
    }
  }, []);

  // Track the dots every frame until the layout animation settles, then stop.
  // Used for *any* layout shift that moves a marker — a swap, a select/clear, or
  // a card growing/shrinking (e.g. an insufficient-funds banner toggling). The
  // final frames land after motion's position animation completes, so the line
  // always converges to the resting layout instead of sticking at a stale length
  // sampled mid-animation. Repeated calls just extend the window: postRender
  // dedupes the same callback and a single rAF watches for the end, so overlapping
  // triggers are safe.
  const loopEndRef = useRef(0);
  const stopRafRef = useRef(0);
  const scheduleMeasure = useCallback(() => {
    if (reduceMotion) {
      measure();
      return;
    }
    loopEndRef.current = performance.now() + SWAP_DURATION * 1000;
    frame.postRender(measure, true); // keepAlive: re-runs every frame until cancelled.
    if (stopRafRef.current) {
      return;
    }
    const watch = () => {
      if (performance.now() >= loopEndRef.current) {
        cancelFrame(measure);
        stopRafRef.current = 0;
        return;
      }
      stopRafRef.current = requestAnimationFrame(watch);
    };
    stopRafRef.current = requestAnimationFrame(watch);
  }, [measure, reduceMotion]);

  useEffect(() => {
    if (previousOrderKey.current === orderKey) {
      return;
    }
    previousOrderKey.current = orderKey;

    const applyDescriptions = () =>
      setDisplayedDescriptions(Object.fromEntries(rowsRef.current.map((row) => [row.key, row.description])));

    if (reduceMotion) {
      labelControls.set({ opacity: 1 });
      applyDescriptions();
      return;
    }
    labelControls.start({
      opacity: [1, 0, 1],
      transition: { duration, times: [0, 0.5, 1], ease: 'easeInOut' },
    });
    // Swap the description text while the labels are faded out (midpoint), so it
    // cross-fades from old to new rather than flipping up front.
    const swap = setTimeout(applyDescriptions, (duration / 2) * 1000);
    return () => clearTimeout(swap);
  }, [orderKey, reduceMotion, duration, labelControls]);

  // Keep the connector synced through swaps (order changes) and select/clear
  // (selection changes): both move the dots. Motion animates the rows via
  // transforms without re-rendering, so the scheduler re-measures on motion's
  // own frame loop with `postRender`, which runs *after* motion writes the rows'
  // transforms for that frame — so the line reads the dots' true positions and
  // stays glued to them, with no first-frame jump or lag. (A plain rAF could
  // fire before motion's render step, sampling stale positions and flashing the
  // resting length for a frame.) Run from a layout effect so the loop is queued
  // before the very first animated frame.
  useLayoutEffect(() => {
    if (previousAnimKey.current === animKey) {
      return;
    }
    previousAnimKey.current = animKey;
    scheduleMeasure();
  }, [animKey, scheduleMeasure]);

  // Cancel any in-flight measurement loop on unmount.
  useEffect(
    () => () => {
      cancelFrame(measure);
      cancelAnimationFrame(stopRafRef.current);
    },
    [measure]
  );

  // Re-measure after every render (covers order, selection, and content
  // changes) and on async size changes such as a card finishing loading, and
  // mount the connector once both markers are present.
  useLayoutEffect(() => {
    if (markerRefs.current.filter(Boolean).length >= 2 && !ready) {
      setReady(true);
    }
    measure();
  });

  useEffect(() => {
    // A size change (card data loading, or an insufficient-funds banner toggling)
    // moves a marker and triggers motion's position animation on the rows below
    // it — so drive the full measurement loop, not a one-shot measure, or the
    // line would stick at the length sampled before the animation settled.
    const observer = new ResizeObserver(() => scheduleMeasure());
    if (listRef.current) {
      observer.observe(listRef.current);
    }
    rowRefs.current.forEach((row) => {
      if (row) {
        observer.observe(row);
      }
    });
    return () => observer.disconnect();
  }, [scheduleMeasure]);

  const layoutTransition: Transition = { duration, ease: [0.4, 0, 0.2, 1] };

  return (
    <Layout className={className}>
      <Inner>
        <List ref={listRef}>
          <ControlFrame ref={frameRef} aria-hidden />
          {ready && (
            <>
              <LineSvg ref={svgRef} aria-hidden>
                <line ref={lineTopRef} x1={1} y1={0} x2={1} y2={0} />
                <line ref={lineBottomRef} x1={1} y1={0} x2={1} y2={0} />
              </LineSvg>
              <FlowIcon ref={iconRef} aria-hidden>
                {reduceMotion ? (
                  <>
                    <FlowChevron $variant="small" />
                    <FlowChevron $variant="large" />
                  </>
                ) : (
                  <>
                    <FlowChevron />
                    <FlowChevron $offset />
                  </>
                )}
              </FlowIcon>
            </>
          )}
          {rows.map((row, index) => (
            <Row
              key={row.key}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              layout="position"
              transition={layoutTransition}
            >
              <Title animate={labelControls} initial={false}>
                <Label>
                  {row.label}
                  {row.required && <Asterix>*</Asterix>}
                </Label>
                {displayedDescriptions[row.key] && <Description>{displayedDescriptions[row.key]}</Description>}
              </Title>
              <Control
                ref={(el) => {
                  controlRefs.current[index] = el;
                }}
              >
                <Rail>
                  <Marker
                    ref={(el) => {
                      markerRefs.current[index] = el;
                    }}
                    $selected={Boolean(row.selected)}
                  >
                    {row.selected ? <Icon.CircleCheck /> : <Icon.CircleBlank />}
                  </Marker>
                </Rail>
                <CardSlot>{row.node}</CardSlot>
              </Control>
            </Row>
          ))}
        </List>
      </Inner>
    </Layout>
  );
};
